import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ExternalLink, Mail, MessageCircle, Phone } from "lucide-react";

const STATUSES = ["novo", "em_contato", "fechado", "perdido"];

export default function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("todos");

  const load = async () => {
    const { data } = await api.get("/admin/quotes");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/quotes/${id}`, { status });
    toast.success("Status atualizado");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este orçamento?")) return;
    await api.delete(`/admin/quotes/${id}`);
    toast.success("Excluído");
    setSelected(null);
    load();
  };

  const filtered = filter === "todos" ? items : items.filter((i) => i.status === filter);

  return (
    <div data-testid="admin-quotes">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-neutral-500 mb-2">Solicitações recebidas</div>
          <h1 className="font-display font-black text-3xl">Orçamentos</h1>
        </div>
        <div className="flex gap-2">
          {["todos", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1.5 text-xs uppercase tracking-widest border ${
                filter === s ? "bg-[#E31B23] border-[#E31B23] text-white" : "border-white/10 text-neutral-400"
              }`}
              data-testid={`quotes-filter-${s}`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,420px] gap-6">
        <div className="rounded-xl border border-white/5 bg-[#0a0a0a] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-neutral-400 uppercase text-xs tracking-widest">
              <tr>
                <th className="text-left p-4">Cliente</th>
                <th className="text-left p-4">Evento</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Data</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-neutral-500">Nenhum orçamento.</td></tr>
              )}
              {filtered.map((q) => (
                <tr key={q.id} onClick={() => setSelected(q)} className="border-t border-white/5 cursor-pointer hover:bg-white/5">
                  <td className="p-4">
                    <div className="font-semibold text-white">{q.name}</div>
                    <div className="text-xs text-neutral-500">{q.email}</div>
                  </td>
                  <td className="p-4">
                    <div>{q.event_type || "-"}</div>
                    <div className="text-xs text-neutral-500">{q.city}</div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-block px-3 py-1 text-[10px] rounded-full uppercase tracking-widest ${
                      q.status === "novo" ? "bg-[#008CFF]/15 text-[#008CFF]" :
                      q.status === "em_contato" ? "bg-yellow-500/15 text-yellow-400" :
                      q.status === "fechado" ? "bg-green-500/15 text-green-400" :
                      "bg-neutral-500/15 text-neutral-400"
                    }`}>{q.status?.replace("_", " ")}</span>
                  </td>
                  <td className="p-4 text-neutral-400 text-xs">{new Date(q.created_at).toLocaleString("pt-BR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-white/5 bg-[#0a0a0a] p-6 lg:sticky lg:top-6 h-fit">
          {!selected && <div className="text-neutral-500 text-sm">Selecione um orçamento para ver os detalhes.</div>}
          {selected && (
            <div>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="font-display font-bold text-xl">{selected.name}</div>
                  {selected.company && <div className="text-neutral-500 text-sm">{selected.company}</div>}
                </div>
                <button onClick={() => remove(selected.id)} className="text-neutral-500 hover:text-[#E31B23]" data-testid="quote-detail-delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2 text-sm">
                <a className="flex items-center gap-2 text-neutral-300 hover:text-white" href={`mailto:${selected.email}`}><Mail className="w-4 h-4 text-[#008CFF]" /> {selected.email}</a>
                <div className="flex items-center gap-2 text-neutral-300"><Phone className="w-4 h-4 text-[#E31B23]" /> {selected.phone}</div>
                {selected.whatsapp && <a className="flex items-center gap-2 text-neutral-300 hover:text-[#25D366]" target="_blank" rel="noreferrer" href={`https://wa.me/${selected.whatsapp.replace(/\D/g, "")}`}><MessageCircle className="w-4 h-4 text-[#25D366]" /> {selected.whatsapp}</a>}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <Info label="Cidade" v={selected.city} />
                <Info label="Tipo" v={selected.event_type} />
                <Info label="Data evento" v={selected.event_date} />
                <Info label="Pessoas" v={selected.people_count} />
              </div>
              {selected.simulators?.length > 0 && (
                <div className="mt-4">
                  <div className="overline text-neutral-500 mb-2">Simuladores</div>
                  <div className="flex flex-wrap gap-2">
                    {selected.simulators.map((s) => (
                      <span key={s} className="rounded-full px-3 py-1 text-xs border border-white/10">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {selected.message && (
                <div className="mt-4">
                  <div className="overline text-neutral-500 mb-2">Mensagem</div>
                  <p className="text-sm text-neutral-300 leading-relaxed">{selected.message}</p>
                </div>
              )}
              <div className="mt-6">
                <div className="overline text-neutral-500 mb-2">Status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(selected.id, s)}
                      className={`rounded-full px-3 py-1.5 text-xs uppercase tracking-widest border ${
                        selected.status === s ? "bg-[#E31B23] border-[#E31B23] text-white" : "border-white/10 text-neutral-400 hover:border-white/40"
                      }`}
                      data-testid={`set-status-${s}`}
                    >
                      {s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, v }) => (
  <div>
    <div className="overline text-neutral-500">{label}</div>
    <div className="text-white">{v || "-"}</div>
  </div>
);
